import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, Area, AreaChart } from "recharts";
import { TrendingUp, Target, Hash, Scale, Trophy, CalendarDays } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { AccountSelect } from "@/components/AccountSelect";
import { EmptyAccounts } from "@/components/EmptyAccounts";
import { SessionHeatmap } from "@/components/SessionHeatmap";
import { useAccounts } from "@/hooks/useAccounts";
import { api } from "@/lib/api";
import { SESSIONS, sessionByKey, fmtPnl, pnlClass } from "@/lib/trading";

const Kpi = ({ label, value, icon: Icon, cls = "text-slate-100", testId, delay }) => (
  <div className="panel p-5 rise" style={{ animationDelay: `${delay}ms` }}>
    <div className="flex justify-between items-start">
      <p className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</p>
      <Icon className="h-4 w-4 text-slate-600" />
    </div>
    <p className={`mt-3 font-mono text-2xl font-semibold ${cls}`} data-testid={testId}>{value}</p>
  </div>
);

const tooltipStyle = { background: "#121722", border: "1px solid #1E293B", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 };

const SessionCards = ({ sessions }) => (
  <div className="grid sm:grid-cols-3 gap-4">
    {SESSIONS.map((s) => {
      const d = sessions[s.key];
      return (
        <div key={s.key} className="panel p-5 border-l-2" style={{ borderLeftColor: s.color }}>
          <div className="flex justify-between text-xs font-mono"><span style={{ color: s.color }}>{s.label}</span><span className="text-slate-500">{s.hours}</span></div>
          <p className={`mt-2 font-mono text-xl font-semibold ${pnlClass(d.pnl)}`} data-testid={`${s.key === "new_york" ? "ny" : s.key}-session-pnl`}>{fmtPnl(d.pnl)}</p>
          <p className="text-xs text-slate-500 font-mono mt-1">{d.trades} trades · {d.win_rate}% win</p>
        </div>
      );
    })}
  </div>
);

export default function Dashboard() {
  const { accounts, loaded } = useAccounts();
  const [acc, setAcc] = useState("all");
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/stats", { params: acc === "all" ? {} : { account_id: acc } }).then((r) => setS(r.data));
  }, [acc]);

  if (loaded && !accounts.length) return (<><PageHeader title="Dashboard" subtitle="Your trading performance at a glance." /><EmptyAccounts /></>);
  if (!s) return <p className="font-mono text-slate-500">Loading…</p>;

  const sessData = SESSIONS.map((x) => ({ name: x.label, pnl: s.sessions[x.key].pnl, color: x.color }));
  const pf = s.profit_factor === "inf" ? "∞" : s.profit_factor ?? "—";

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Performance across sessions and days.">
        <AccountSelect accounts={accounts} value={acc} onChange={setAcc} allowAll testId="dashboard-account-filter" />
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <Kpi label="Total P&L" value={fmtPnl(s.total_pnl)} cls={pnlClass(s.total_pnl)} icon={TrendingUp} testId="total-pnl-value" delay={0} />
        <Kpi label="Win rate" value={`${s.win_rate}%`} icon={Target} testId="win-rate-value" delay={50} />
        <Kpi label="Trades" value={s.total_trades} icon={Hash} testId="total-trades-value" delay={100} />
        <Kpi label="Profit factor" value={pf} icon={Scale} testId="profit-factor-value" delay={150} />
        <Kpi label="Best session" value={s.best_session ? sessionByKey[s.best_session].label : "—"} icon={Trophy} testId="best-session-value" delay={200} />
        <Kpi label="Avg / day" value={fmtPnl(s.avg_daily)} cls={pnlClass(s.avg_daily)} icon={CalendarDays} testId="avg-daily-value" delay={250} />
      </div>
      <SessionCards sessions={s.sessions} />
      <div className="grid lg:grid-cols-5 gap-4 mt-6">
        <div className="panel p-5 lg:col-span-2" data-testid="session-pnl-chart">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-4">P&L by session</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={sessData}>
              <CartesianGrid stroke="#1E293B" vertical={false} />
              <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#1E293B55" }} formatter={(v) => fmtPnl(v)} />
              <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>{sessData.map((d) => <Cell key={d.name} fill={d.color} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="panel p-5 lg:col-span-3" data-testid="cumulative-chart">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-4">Cumulative P&L</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={s.daily}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#1E293B" vertical={false} />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtPnl(v)} />
              <Area type="monotone" dataKey="cumulative" stroke="#10B981" fill="url(#g)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <SessionHeatmap heatmap={s.heatmap} />
      <DailyTable daily={s.daily} />
    </>
  );
}

const DailyTable = ({ daily }) => (
  <div className="panel p-5 mt-6" data-testid="daily-earnings-table">
    <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-4">Daily earnings</p>
    {!daily.length ? <p className="text-sm text-slate-500">No trades yet.</p> : (
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {[...daily].reverse().slice(0, 28).map((d) => (
          <div key={d.date} data-testid={`daily-cell-${d.date}`} className={`rounded-md border p-3 ${d.pnl >= 0 ? "border-emerald-800/40 bg-emerald-500/5" : "border-red-800/40 bg-red-500/5"}`}>
            <p className="text-[11px] font-mono text-slate-500">{d.date}</p>
            <p className={`font-mono text-sm font-semibold ${pnlClass(d.pnl)}`}>{fmtPnl(d.pnl)}</p>
            <p className="text-[11px] font-mono text-slate-600">{d.trades} trades</p>
          </div>
        ))}
      </div>
    )}
  </div>
);
